import type { RouteLocationNormalized } from 'vue-router'
import NAV_MAIN from '@/constants/nav/main'
import type { NavMainItem } from '@/types/nav/main'
import { find, flatMap, startsWith } from 'lodash'
import { useCookies } from '@vueuse/integrations/useCookies'
import queryClient from '@/plugins/query'
import { QUERY_KEYS } from '@/constants/query'
import useApi from '@/composables/api'
import NAV_SETTINGS from '@/constants/nav/settings'
import type { NavSettingsItem } from '@/types/nav/settings'

const { api } = useApi()
const cookies = useCookies()

export default async (to: RouteLocationNormalized) => {
    const userHasAccessToken = !!cookies.get(`access_token`)

    // Proceed with paid Breeze check only for authenticated users
    if (!userHasAccessToken) {
        return
    }

    try {
        // Get all nav items that could require a paid plan
        const allNavItems = [...NAV_MAIN, ...flatMap(NAV_SETTINGS)]

        // Find if any `isPaid: true` nav items match the requested route (or sub-route).
        // Unfortunately we can't use `to.match` because not all Breeze routes are defined in Vue
        const matchingPaidNavItem: NavMainItem | NavSettingsItem | undefined = find(
            allNavItems,
            navItem => !!(navItem.route && navItem.isPaid && startsWith(to.path, navItem.route))
        )

        // If no `isPaid` nav item matches, allow navigation
        if (!matchingPaidNavItem) {
            return
        }

        // Else fetch `me` data since we're requesting access to a paid feature
        const { data: meData } = await queryClient.ensureQueryData({
            queryKey: QUERY_KEYS.me,
            queryFn: () => api.authorizationLoggedInUser.getMe()
        })

        // Block route if user isn't on a paid plan
        if (!meData.organization.paid) {
            return { path: '/errors/freebreeze' }
        }
    } catch (error) {
        // Let the API middleware handle it
        return
    }
}
